import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Trash2,
  Package,
  DollarSign,
  Clock,
  Tag
} from "lucide-react"
import type { Product, Category } from "@/types"
import { useCurrency } from "@/contexts/CurrencyContext"
import { cn } from "@/lib/utils"
import { ProductImagePlaceholder } from "@/components/menu/ProductImagePlaceholder"

/** Product + Category columns stay left-aligned; all others centered. */
function menuProductColLeftAligned(columnId: string) {
  return columnId === "select" || columnId === "name" || columnId === "category_id"
}

/**
 * Progressive column hiding for narrow screens (mirrors AdminCustomersDirectory).
 * Must return the same class for a column's TableHead and TableCell or columns shift.
 */
function menuProductColResponsiveClass(columnId: string) {
  // Hold the lower-priority columns until there's room for them, so the
  // always-visible Availability toggle stays on-screen at the tablet band
  // (collapsed rail leaves ~700px) instead of being pushed off the edge.
  if (columnId === "category_id") return "hidden lg:table-cell"
  if (columnId === "preparation_time") return "hidden xl:table-cell"
  return ""
}

interface AdminMenuTableProps {
  data: Product[]
  categories: Category[]
  onEdit: (product: Product) => void
  onDelete: (product: Product) => void
  onToggleAvailability: (product: Product) => void
  /** Toggle whether the item shows on the online-ordering storefront (068). */
  onToggleStorefrontVisible: (product: Product) => void
  /** Show the Storefront column — only for tenants with online ordering on. */
  showStorefrontColumn?: boolean
  isLoading?: boolean
  selectedIds: Set<string>
  onToggleSelect: (productId: string) => void
  onToggleSelectAllPage: () => void
  allPageSelected: boolean
}

export function AdminMenuTable({
  data,
  categories,
  onEdit,
  onDelete,
  onToggleAvailability,
  onToggleStorefrontVisible,
  showStorefrontColumn = false,
  isLoading = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAllPage,
  allPageSelected,
}: AdminMenuTableProps) {
  const { formatCurrency } = useCurrency()
  const [sorting, setSorting] = useState<SortingState>([])

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return "No Category"
    const category = categories.find(cat => cat.id === categoryId)
    return category?.name || "Unknown Category"
  }

  const getCategoryColor = (categoryId: string | null): string => {
    if (!categoryId) return "#6B7280"
    const category = categories.find(cat => cat.id === categoryId)
    return category?.color || "#6B7280"
  }

  const columns: ColumnDef<Product>[] = [
    {
      id: "select",
      header: () => (
        <div className="flex justify-start">
          <input
            type="checkbox"
            checked={allPageSelected}
            onChange={onToggleSelectAllPage}
            disabled={isLoading || data.length === 0}
            className="rounded border-input cursor-pointer"
            aria-label="Select all products on this page"
          />
        </div>
      ),
      cell: ({ row }) => {
        const id = String(row.original.id)
        return (
          <div className="flex justify-start">
            <input
              type="checkbox"
              checked={selectedIds.has(id)}
              onChange={() => onToggleSelect(id)}
              className="rounded border-input cursor-pointer"
              aria-label={`Select ${row.original.name}`}
            />
          </div>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 lg:px-3"
          >
            <Package className="mr-2 h-4 w-4" />
            Product
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ row }) => {
        const product = row.original
        return (
          <div className="flex items-center justify-start gap-3">
            <div className="flex-shrink-0">
              {product.image_url ? (
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className="h-12 w-12 rounded-lg object-cover"
                />
              ) : (
                <ProductImagePlaceholder className="h-12 w-12 rounded-lg" iconClassName="h-6 w-6" />
              )}
            </div>
            <div>
              <div className="font-medium text-foreground">
                {product.name}
              </div>
              <div className="text-sm text-muted-foreground line-clamp-1">
                {product.description || "No description"}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: "category_id",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 lg:px-3"
          >
            <Tag className="mr-2 h-4 w-4" />
            Category
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ getValue }) => {
        const categoryId = getValue() as string | null
        const categoryName = getCategoryName(categoryId)
        const categoryColor = getCategoryColor(categoryId)
        return (
          <div className="flex justify-start">
            <Badge 
              style={{ backgroundColor: categoryColor, color: 'white' }}
              className="border-0"
            >
              {categoryName}
            </Badge>
          </div>
        )
      },
    },
    {
      accessorKey: "price",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="mx-auto h-8 px-2 lg:px-3"
          >
            <DollarSign className="mr-2 h-4 w-4" />
            Price
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ getValue }) => {
        const price = getValue() as number
        return (
          <div className="flex justify-center font-medium text-green-600 tabular-nums">
            {formatCurrency(price)}
          </div>
        )
      },
    },
    {
      accessorKey: "preparation_time",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="mx-auto h-8 px-2 lg:px-3"
          >
            <Clock className="mr-2 h-4 w-4" />
            Prep Time
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ getValue }) => {
        const time = getValue() as number
        return (
          <div className="flex justify-center text-foreground tabular-nums">
            {time}min
          </div>
        )
      },
    },
    {
      accessorKey: "is_available",
      header: () => <span className="flex w-full justify-center">Availability</span>,
      cell: ({ row }) => {
        const product = row.original
        return (
          <div className="flex justify-center">
            <Switch
              checked={product.is_available}
              onCheckedChange={() => onToggleAvailability(product)}
              aria-label={`Toggle availability for ${product.name}`}
            />
          </div>
        )
      },
    },
    // Storefront visibility toggle — only rendered for tenants with online
    // ordering enabled (068). Undefined reads as visible (pre-068 rows).
    ...(showStorefrontColumn
      ? [{
          id: "storefront_visible",
          header: () => <span className="flex w-full justify-center">Storefront</span>,
          cell: ({ row }: { row: { original: Product } }) => {
            const product = row.original
            return (
              <div className="flex justify-center">
                <Switch
                  checked={product.is_storefront_visible !== false}
                  onCheckedChange={() => onToggleStorefrontVisible(product)}
                  aria-label={`Toggle storefront visibility for ${product.name}`}
                />
              </div>
            )
          },
        } as ColumnDef<Product>]
      : []),
    {
      id: "actions",
      header: () => <span className="flex w-full justify-center">Actions</span>,
      cell: ({ row }) => {
        const product = row.original
        return (
          <div className="flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(product)}
              className="h-8 px-2 lg:px-3"
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only lg:not-sr-only lg:ml-2">Edit</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(product)}
              className="h-8 px-2 lg:px-3 text-red-600 hover:text-danger-fg hover:border-red-300"
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only lg:not-sr-only lg:ml-2">Delete</span>
            </Button>
          </div>
        )
      },
    },
  ]

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  return (
    <div className="w-full min-w-0">
      <div className="overflow-x-auto rounded-md border bg-card [-webkit-overflow-scrolling:touch]">
        <Table className="min-w-[520px] md:min-w-[640px] lg:min-w-[960px]">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                               {headerGroup.headers.map((header) => {
                  const cid = header.column.id
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "px-4 align-middle",
                        menuProductColLeftAligned(cid) ? "text-left" : "text-center",
                        cid === "select" ? "w-12" : "",
                        menuProductColResponsiveClass(cid),
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((col, j) => {
                    const cid = String(
                      col.id ?? ("accessorKey" in col ? (col as { accessorKey?: string }).accessorKey : "") ?? "",
                    )
                    const left = menuProductColLeftAligned(cid)
                    return (
                      <TableCell
                        key={j}
                        className={cn(
                          "align-middle",
                          left ? "text-left" : "text-center",
                          menuProductColResponsiveClass(cid),
                        )}
                      >
                        <div
                          className={cn(
                            "h-4 rounded bg-muted animate-pulse",
                            left ? "max-w-[90%]" : "mx-auto max-w-[80%]",
                          )}
                        />
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={`hover:bg-muted/50 ${selectedIds.has(String(row.original.id)) ? "bg-primary/5" : ""}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "align-middle",
                        menuProductColLeftAligned(cell.column.id) ? "text-left" : "text-center",
                        menuProductColResponsiveClass(cell.column.id),
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground">No products found</p>
                    <p className="text-sm text-muted-foreground">Try adjusting your search or add a new product</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
